import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    ChevronLeft, 
    ChevronRight, 
    Plus, 
    Edit2, 
    Trash2, 
    X, 
    Save,
    HardHat,
    LogOut,
    LayoutGrid,
    Clipboard,
    Megaphone,
    Target,
    FileText,
    ExternalLink,
    Eye,
    Filter,
    CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { postsAPI } from '../lib/api';
import { toast } from 'sonner';

// Helpers
const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const formatDate = (date) => date.toISOString().split('T')[0];

const getWeekDates = (year, week) => {
    const jan4 = new Date(year, 0, 4);
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
    const targetMonday = new Date(week1Monday);
    targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(targetMonday);
        d.setDate(targetMonday.getDate() + i);
        dates.push(d);
    }
    return dates;
};

const DIAS_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const REDES = [
    { value: 'instagram', label: 'Instagram', color: 'bg-gradient-to-r from-purple-500 to-pink-500', solid: 'bg-pink-500' },
    { value: 'facebook', label: 'Facebook', color: 'bg-blue-600', solid: 'bg-blue-600' },
    { value: 'linkedin', label: 'LinkedIn', color: 'bg-blue-700', solid: 'bg-blue-700' },
    { value: 'tiktok', label: 'TikTok', color: 'bg-slate-900', solid: 'bg-slate-900' },
    { value: 'twitter', label: 'Twitter/X', color: 'bg-slate-800', solid: 'bg-slate-800' },
    { value: 'multiple', label: 'Múltiples', color: 'bg-purple-600', solid: 'bg-purple-600' }
];
const TIPOS = ['imagen', 'video', 'carrusel', 'reel', 'story'];
const ESTADOS = [
    { value: 'borrador', label: 'Borrador', color: 'bg-slate-100 text-slate-600 border-slate-300' },
    { value: 'pendiente', label: 'Pendiente', color: 'bg-amber-50 text-amber-700 border-amber-300' },
    { value: 'aprobado', label: 'Aprobado', color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
    { value: 'publicado', label: 'Publicado', color: 'bg-blue-50 text-blue-700 border-blue-300' }
];

const getRedConfig = (red) => REDES.find(r => r.value === red) || REDES[0];
const getEstadoConfig = (estado) => ESTADOS.find(e => e.value === estado) || ESTADOS[0];

const GrillaPage = () => {
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentWeek, setCurrentWeek] = useState(getWeekNumber(new Date()));
    
    // Filter states
    const [filterRed, setFilterRed] = useState('all');
    const [filterEstado, setFilterEstado] = useState('all');
    const [filterPromocionado, setFilterPromocionado] = useState('all');
    
    // Modal states
    const [showForm, setShowForm] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    
    const [formData, setFormData] = useState({
        fecha: formatDate(new Date()),
        red_social: 'instagram',
        tipo: 'imagen',
        titulo: '',
        descripcion: '',
        objetivo: '',
        copy: '',
        promocionado: false,
        estado: 'borrador'
    });

    const weekDates = getWeekDates(currentYear, currentWeek);

    useEffect(() => {
        fetchPosts();
    }, [currentYear, currentWeek]);
    
    // Filter posts
    const filteredPosts = posts.filter(post => {
        if (filterRed !== 'all' && post.red_social !== filterRed) return false;
        if (filterEstado !== 'all' && post.estado !== filterEstado) return false;
        if (filterPromocionado !== 'all') {
            if (filterPromocionado === 'si' && !post.promocionado) return false;
            if (filterPromocionado === 'no' && post.promocionado) return false;
        }
        return true;
    });
    
    const activeFiltersCount = [filterRed, filterEstado, filterPromocionado].filter(f => f !== 'all').length;
    
    const clearFilters = () => {
        setFilterRed('all');
        setFilterEstado('all');
        setFilterPromocionado('all');
    };

    const fetchPosts = async () => {
        setLoading(true);
        try {
            const startDate = formatDate(weekDates[0]);
            const endDate = formatDate(weekDates[6]);
            const response = await postsAPI.getAll({ fecha_inicio: startDate, fecha_fin: endDate });
            setPosts(response.data);
        } catch (error) {
            toast.error('Error al cargar publicaciones');
        } finally {
            setLoading(false);
        }
    };

    const goToPrevWeek = () => {
        if (currentWeek === 1) { setCurrentYear(y => y - 1); setCurrentWeek(52); }
        else setCurrentWeek(w => w - 1);
    };

    const goToNextWeek = () => {
        if (currentWeek === 52) { setCurrentYear(y => y + 1); setCurrentWeek(1); }
        else setCurrentWeek(w => w + 1);
    };

    const goToToday = () => {
        setCurrentYear(new Date().getFullYear());
        setCurrentWeek(getWeekNumber(new Date()));
    };

    const handleNewPost = (fecha = null) => {
        setEditingPost(null);
        setFormData({
            fecha: fecha || formatDate(new Date()),
            red_social: 'instagram',
            tipo: 'imagen',
            titulo: '',
            descripcion: '',
            objetivo: '',
            copy: '',
            promocionado: false,
            estado: 'borrador'
        });
        setShowForm(true);
    };

    const handleEditPost = (post) => {
        setEditingPost(post);
        setFormData({
            fecha: post.fecha,
            red_social: post.red_social,
            tipo: post.tipo,
            titulo: post.titulo,
            descripcion: post.descripcion || '',
            objetivo: post.objetivo || '',
            copy: post.copy || '',
            promocionado: post.promocionado,
            estado: post.estado
        });
        setShowDetail(false);
        setShowForm(true);
    };

    const handleViewPost = (post) => {
        setSelectedPost(post);
        setShowDetail(true);
    };

    const handleSave = async () => {
        if (!formData.titulo.trim()) { toast.error('El título es requerido'); return; }
        try {
            if (editingPost) {
                await postsAPI.update(editingPost.id, formData);
                toast.success('Publicación actualizada');
            } else {
                await postsAPI.create(formData);
                toast.success('Publicación creada');
            }
            setShowForm(false);
            fetchPosts();
        } catch (error) {
            toast.error('Error al guardar');
        }
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('¿Eliminar esta publicación?')) return;
        try {
            await postsAPI.delete(postId);
            toast.success('Publicación eliminada');
            setShowDetail(false);
            fetchPosts();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const goToProduccion = (postId) => {
        navigate(`/produccion?highlight=${postId}`);
    };

    const postsByDate = filteredPosts.reduce((acc, post) => {
        if (!acc[post.fecha]) acc[post.fecha] = [];
        acc[post.fecha].push(post);
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-slate-100">
            {/* Header */}
            <header className="bg-[#0B3D91] text-white shadow-lg">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#FF6B00] rounded flex items-center justify-center">
                                <HardHat className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide">Segumax</h1>
                                <p className="text-xs text-white/60 uppercase tracking-wider">Planner</p>
                            </div>
                        </div>
                        
                        <nav className="flex items-center gap-1">
                            <Link to="/" className="flex items-center gap-2 px-4 py-2 rounded bg-white/20 text-white text-sm font-medium">
                                <LayoutGrid className="w-4 h-4" />
                                <span>Semanal</span>
                            </Link>
                            <Link to="/mensual" className="flex items-center gap-2 px-4 py-2 rounded text-white/70 hover:bg-white/10 text-sm font-medium transition-colors">
                                <CalendarDays className="w-4 h-4" />
                                <span>Mensual</span>
                            </Link>
                            <Link to="/produccion" className="flex items-center gap-2 px-4 py-2 rounded text-white/70 hover:bg-white/10 text-sm font-medium transition-colors">
                                <Clipboard className="w-4 h-4" />
                                <span>Producción</span>
                            </Link>
                        </nav>

                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <p className="text-sm font-medium">{user?.name}</p>
                                <p className="text-xs text-white/60 uppercase">{isAdmin() ? 'Administrador' : 'Cliente'}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => { logout(); window.location.href = '/login'; }} className="text-white/70 hover:text-white hover:bg-white/10">
                                <LogOut className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Week Navigation */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button variant="outline" size="icon" onClick={goToPrevWeek} className="h-10 w-10" data-testid="btn-prev-week">
                                <ChevronLeft className="w-5 h-5" />
                            </Button>
                            <div className="text-center min-w-[220px]">
                                <p className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800 uppercase tracking-wide">
                                    Semana {currentWeek}
                                </p>
                                <p className="text-sm text-slate-500">
                                    {weekDates[0].toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })} - {weekDates[6].toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </p>
                            </div>
                            <Button variant="outline" size="icon" onClick={goToNextWeek} className="h-10 w-10" data-testid="btn-next-week">
                                <ChevronRight className="w-5 h-5" />
                            </Button>
                            <Button variant="outline" onClick={goToToday} className="text-sm">Hoy</Button>
                        </div>
                        
                        {isAdmin() && (
                            <Button onClick={() => handleNewPost()} className="bg-[#FF6B00] hover:bg-[#e65000] text-white font-semibold px-6" data-testid="btn-new-post">
                                <Plus className="w-4 h-4 mr-2" /> Nueva Publicación
                            </Button>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-6">
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-slate-500" />
                            <span className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Filtros:</span>
                        </div>
                        
                        <select 
                            value={filterRed} 
                            onChange={(e) => setFilterRed(e.target.value)}
                            className="h-9 px-3 border border-slate-200 rounded text-sm bg-white"
                            data-testid="filter-red"
                        >
                            <option value="all">Todas las redes</option>
                            {REDES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        
                        <select 
                            value={filterEstado} 
                            onChange={(e) => setFilterEstado(e.target.value)}
                            className="h-9 px-3 border border-slate-200 rounded text-sm bg-white"
                            data-testid="filter-estado"
                        >
                            <option value="all">Todos los estados</option>
                            {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                        </select>
                        
                        <select 
                            value={filterPromocionado} 
                            onChange={(e) => setFilterPromocionado(e.target.value)}
                            className="h-9 px-3 border border-slate-200 rounded text-sm bg-white"
                            data-testid="filter-promocionado"
                        >
                            <option value="all">Promocionado: Todos</option>
                            <option value="si">Solo ADS</option>
                            <option value="no">Sin ADS</option>
                        </select>
                        
                        {activeFiltersCount > 0 && (
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={clearFilters}
                                className="text-slate-500 hover:text-slate-700"
                            >
                                <X className="w-4 h-4 mr-1" />
                                Limpiar ({activeFiltersCount})
                            </Button>
                        )}
                        
                        <div className="ml-auto text-sm text-slate-500">
                            {filteredPosts.length} de {posts.length} publicaciones
                        </div>
                    </div>
                </div>

                {/* Grid Table */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    {/* Days Header */}
                    <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
                        {weekDates.map((date, idx) => {
                            const isToday = formatDate(date) === formatDate(new Date());
                            return (
                                <div key={idx} className={`px-4 py-4 text-center border-r last:border-r-0 border-slate-200 ${isToday ? 'bg-[#0B3D91]' : ''}`}>
                                    <p className={`text-xs font-semibold uppercase tracking-wider ${isToday ? 'text-white/70' : 'text-slate-500'}`}>{DIAS_SHORT[idx]}</p>
                                    <p className={`text-2xl font-['Barlow_Condensed'] font-bold mt-1 ${isToday ? 'text-white' : 'text-slate-800'}`}>{date.getDate()}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Content Grid */}
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="w-8 h-8 border-4 border-[#0B3D91] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-slate-500">Cargando...</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-7 min-h-[500px]">
                            {weekDates.map((date, idx) => {
                                const dateStr = formatDate(date);
                                const dayPosts = postsByDate[dateStr] || [];
                                const isToday = dateStr === formatDate(new Date());
                                
                                return (
                                    <div key={idx} className={`border-r last:border-r-0 border-slate-200 p-2 ${isToday ? 'bg-blue-50/50' : ''}`}>
                                        <div className="space-y-2">
                                            {dayPosts.map((post) => {
                                                const redConfig = getRedConfig(post.red_social);
                                                const estadoConfig = getEstadoConfig(post.estado);
                                                
                                                return (
                                                    <div 
                                                        key={post.id}
                                                        onClick={() => handleViewPost(post)}
                                                        className="bg-white rounded border border-slate-200 shadow-sm hover:shadow-md hover:border-[#0B3D91]/30 transition-all cursor-pointer overflow-hidden"
                                                        data-testid={`post-${post.id}`}
                                                    >
                                                        {/* Red Social Header */}
                                                        <div className={`${redConfig.solid} px-2 py-1 flex items-center justify-between`}>
                                                            <span className="text-white text-[10px] font-bold uppercase tracking-wider">{redConfig.label}</span>
                                                            {post.promocionado && (
                                                                <Megaphone className="w-3 h-3 text-amber-300" />
                                                            )}
                                                        </div>
                                                        
                                                        {/* Content */}
                                                        <div className="p-2">
                                                            <p className="font-semibold text-slate-800 text-xs leading-tight line-clamp-2">{post.titulo}</p>
                                                            {post.objetivo && (
                                                                <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                                                                    <Target className="w-3 h-3" />
                                                                    <span className="truncate">{post.objetivo}</span>
                                                                </p>
                                                            )}
                                                            <div className="flex items-center justify-between mt-2">
                                                                <span className="text-[10px] text-slate-500 capitalize">{post.tipo}</span>
                                                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${estadoConfig.color}`}>
                                                                    {estadoConfig.label}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            
                                            {/* Add Button */}
                                            {isAdmin() && (
                                                <button
                                                    onClick={() => handleNewPost(dateStr)}
                                                    className="w-full py-2 border-2 border-dashed border-slate-200 rounded text-slate-400 hover:border-[#FF6B00] hover:text-[#FF6B00] transition-all flex items-center justify-center"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-slate-500">
                    <span className="font-semibold text-slate-700">Redes:</span>
                    {REDES.map(red => (
                        <span key={red.value} className="flex items-center gap-2">
                            <span className={`${red.solid} w-4 h-4 rounded`}></span>
                            <span>{red.label}</span>
                        </span>
                    ))}
                </div>
            </main>

            {/* DETAIL MODAL */}
            <Dialog open={showDetail} onOpenChange={setShowDetail}>
                <DialogContent className="max-w-2xl">
                    {selectedPost && (
                        <>
                            <DialogHeader>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`${getRedConfig(selectedPost.red_social).solid} text-white px-3 py-1 rounded text-xs font-bold uppercase`}>
                                                {getRedConfig(selectedPost.red_social).label}
                                            </span>
                                            <span className="text-xs text-slate-500 capitalize">{selectedPost.tipo}</span>
                                            {selectedPost.promocionado && (
                                                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
                                                    <Megaphone className="w-3 h-3" /> ADS
                                                </span>
                                            )}
                                        </div>
                                        <DialogTitle className="text-xl font-bold">{selectedPost.titulo}</DialogTitle>
                                    </div>
                                    <span className={`text-xs font-semibold px-3 py-1 rounded border ${getEstadoConfig(selectedPost.estado).color}`}>
                                        {getEstadoConfig(selectedPost.estado).label}
                                    </span>
                                </div>
                            </DialogHeader>
                            
                            <div className="space-y-4 mt-4">
                                {/* Fecha */}
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <span className="font-medium">Fecha:</span>
                                    <span>{new Date(selectedPost.fecha + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                </div>

                                {/* Objetivo */}
                                {selectedPost.objetivo && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <div className="flex items-center gap-2 text-amber-700 mb-2">
                                            <Target className="w-4 h-4" />
                                            <span className="text-xs font-bold uppercase tracking-wider">Objetivo</span>
                                        </div>
                                        <p className="text-slate-700">{selectedPost.objetivo}</p>
                                    </div>
                                )}

                                {/* Descripción */}
                                {selectedPost.descripcion && (
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Descripción</p>
                                        <p className="text-slate-700">{selectedPost.descripcion}</p>
                                    </div>
                                )}

                                {/* Copy */}
                                {selectedPost.copy && (
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <div className="flex items-center gap-2 text-slate-600 mb-2">
                                            <FileText className="w-4 h-4" />
                                            <span className="text-xs font-bold uppercase tracking-wider">Copy</span>
                                        </div>
                                        <p className="text-slate-700 whitespace-pre-wrap">{selectedPost.copy}</p>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                                    <Button 
                                        variant="outline" 
                                        onClick={() => goToProduccion(selectedPost.id)}
                                        className="flex-1"
                                    >
                                        <Clipboard className="w-4 h-4 mr-2" />
                                        Ver Producción
                                    </Button>
                                    
                                    {isAdmin() && (
                                        <>
                                            <Button 
                                                variant="outline" 
                                                onClick={() => handleEditPost(selectedPost)}
                                            >
                                                <Edit2 className="w-4 h-4 mr-2" />
                                                Editar
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                onClick={() => handleDelete(selectedPost.id)}
                                                className="text-red-600 hover:bg-red-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* FORM MODAL */}
            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50 sticky top-0">
                            <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 uppercase tracking-wide">
                                {editingPost ? 'Editar Publicación' : 'Nueva Publicación'}
                            </h2>
                            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Fecha</label>
                                    <Input type="date" value={formData.fecha} onChange={(e) => setFormData({...formData, fecha: e.target.value})} className="h-11" data-testid="input-fecha" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Red Social</label>
                                    <select value={formData.red_social} onChange={(e) => setFormData({...formData, red_social: e.target.value})} className="w-full h-11 border border-slate-200 rounded-md px-3 text-sm" data-testid="select-red">
                                        {REDES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Tipo</label>
                                    <select value={formData.tipo} onChange={(e) => setFormData({...formData, tipo: e.target.value})} className="w-full h-11 border border-slate-200 rounded-md px-3 text-sm capitalize" data-testid="select-tipo">
                                        {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Estado</label>
                                    <select value={formData.estado} onChange={(e) => setFormData({...formData, estado: e.target.value})} className="w-full h-11 border border-slate-200 rounded-md px-3 text-sm" data-testid="select-estado">
                                        {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Título</label>
                                <Input value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} placeholder="Título de la publicación" className="h-11" data-testid="input-titulo" />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                    <Target className="w-3 h-3 inline mr-1" /> Objetivo
                                </label>
                                <Input value={formData.objetivo} onChange={(e) => setFormData({...formData, objetivo: e.target.value})} placeholder="¿Cuál es el objetivo de esta publicación?" data-testid="input-objetivo" />
                            </div>
                            
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Descripción</label>
                                <Textarea value={formData.descripcion} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} placeholder="Descripción breve..." rows={2} data-testid="input-descripcion" />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                    <FileText className="w-3 h-3 inline mr-1" /> Copy
                                </label>
                                <Textarea value={formData.copy} onChange={(e) => setFormData({...formData, copy: e.target.value})} placeholder="Texto que irá en la publicación..." rows={4} data-testid="input-copy" />
                            </div>
                            
                            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <input type="checkbox" id="promocionado" checked={formData.promocionado} onChange={(e) => setFormData({...formData, promocionado: e.target.checked})} className="w-5 h-5 rounded" data-testid="check-promocionado" />
                                <label htmlFor="promocionado" className="flex items-center gap-2 text-sm font-medium text-amber-800">
                                    <Megaphone className="w-4 h-4" /> Publicación promocionada (ADS)
                                </label>
                            </div>
                        </div>
                        
                        <div className="flex gap-3 p-5 border-t border-slate-200 bg-slate-50 sticky bottom-0">
                            <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1 h-11">Cancelar</Button>
                            <Button onClick={handleSave} className="flex-1 h-11 bg-[#FF6B00] hover:bg-[#e65000] text-white font-semibold" data-testid="btn-save">
                                <Save className="w-4 h-4 mr-2" /> {editingPost ? 'Guardar Cambios' : 'Crear Publicación'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GrillaPage;
